import React from 'react';

import ControlledInput from '@reverbdotcom/commons/dist/components/controlled_input';
import ControlledCheckbox from '@reverbdotcom/commons/dist/components/controlled_checkbox';
import {
  core_apimessages_Country as ICountry,
  core_apimessages_Error as IError,
  core_apimessages_MyAddressType,
} from '../../schemas/rql';
import { CountrySelector } from '../discovery/country_selector';
import RegionSelect from './region_select';
import { IAddressFormData } from './address_form_data';
import bind from '@reverbdotcom/commons/dist/bind';
import AlertBox from '@reverbdotcom/commons/dist/components/alert_box';
import { decamelizeKeys } from 'humps';
import I18n from 'i18n-js';
import AddressFormFields from '../address_form_fields';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/dist/components/user_context_provider';
import { isExperimentEnabled } from '@reverbdotcom/commons/dist/user_context_helpers';
import experiments from '@reverbdotcom/commons/dist/experiments';
import LabelTag from '@reverbdotcom/commons/dist/components/label_tag';
import FormGroupWithInput from '@reverbdotcom/commons/dist/components/form_group_with_input';

interface IProps {
  addressFormData: IAddressFormData;
  countries: ICountry[];
  onUpdateField: Function;
  addressIsCurrentPrimary: boolean;
  errors: IError[];
  formRef(instance: HTMLFormElement): any;
  addressType?: core_apimessages_MyAddressType;
  shouldTrackView?: boolean;
}

export class AddressForm extends React.Component<IProps & IUserContext, null> {
  @bind
  onCountryChange(countryCode) {
    const addressAttributes: Partial<IAddressFormData> = {
      countryCode,
      region: '',
    };
    this.props.onUpdateField(addressAttributes);
  }

  @bind
  handleChangeRegion(e) {
    const addressAttributes: Partial<IAddressFormData> = {
      region: e.target.value,
    };
    this.props.onUpdateField(addressAttributes);
  }

  get isEuOdrAddress(): boolean {
    return this.props.addressType === core_apimessages_MyAddressType.EU_ODR_PLATFORM;
  }

  renderEmailInput() {
    if (!this.isEuOdrAddress) {
      return null;
    }

    return (
      <div className="scaling-mt-2">
        <FormGroupWithInput
          label={I18n.t('discovery.addressBook.form.email')}
          id="address-email"
          fieldName="email"
          inputName="email"
          inputType="text"
          value={this.props.addressFormData.email}
          updateField={this.props.onUpdateField}
          required
        />
      </div>
    );
  }

  renderDefaultCheckbox() {
    if (this.isEuOdrAddress) {
      return null;
    }

    if (this.props.addressIsCurrentPrimary) {
      return (
        <p className="scaling-mt-2 mb-0">
          <LabelTag>
            <span className="icon-l-star">
              {I18n.t('discovery.addressBook.currentlyDefault')}
            </span>
          </LabelTag>
        </p>
      );
    }

    return (
      <div className="label-with-checkbox">
        <ControlledCheckbox
          fieldName="primary"
          inputName="address_primary"
          checked={this.props.addressFormData.primary}
          updateField={this.props.onUpdateField}
        />
        <label data-make-primary htmlFor="address_primary">
          <div>
            {I18n.t('discovery.addressBook.form.makeDefault')}
            <p className="form-group__help-text mb-0">
              {I18n.t('discovery.addressBook.form.makeDefaultHint')}
            </p>
          </div>
        </label>
      </div>
    );
  }

  renderError(error: IError) {
    return (
      <li key={error.field + error.message}>
        {error.message}
      </li>
    );
  }

  renderErrors() {
    if (this.props.errors.length === 0) { return null; }

    return (
      <div className="mb-1">
        <AlertBox type="error">
          {this.props.errors.map(e => this.renderError(e))}
        </AlertBox>
      </div>
    );
  }

  existingAddressForm() {
    if (isExperimentEnabled(this.props.user, experiments.INTERNATIONAL_ADDRESS_FORM_CHECKOUT)) {
      return null;
    }

    return (
      <>
        <div className="form-group">
          <div className="form-group__header">
            <label htmlFor="address-name">
              {I18n.t('discovery.addressBook.form.name')}
            </label>
          </div>
          <div data-address-form-name className="form-group__fields">
            <ControlledInput
              id="address-name"
              fieldName="name"
              inputName="name"
              inputType="text"
              inputValue={this.props.addressFormData.name}
              updateField={this.props.onUpdateField}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <div className="form-group__header">
            <label htmlFor="address-country">
              {I18n.t('discovery.addressBook.form.country')}
            </label>
          </div>
          <div className="form-group__fields">
            <div data-address-form-countrycode>
              <CountrySelector
                id="address-country"
                inputName="countryCode"
                onChange={this.onCountryChange}
                value={this.props.addressFormData.countryCode}
                countries={decamelizeKeys(this.props.countries)}
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <div className="form-group__header">
            <label htmlFor="address-street-address">
              {I18n.t('discovery.addressBook.form.streetAddress')}
            </label>
          </div>
          <div className="form-group__fields">
            <ControlledInput
              id="address-street-address"
              fieldName="streetAddress"
              inputName="streetAddress"
              inputType="text"
              inputValue={this.props.addressFormData.streetAddress}
              updateField={this.props.onUpdateField}
              required
            />
            {!this.isEuOdrAddress && <ControlledInput
              fieldName="extendedAddress"
              inputName="extendedAddress"
              inputType="text"
              inputValue={this.props.addressFormData.extendedAddress}
              className="mt-half"
              updateField={this.props.onUpdateField}
            />}
          </div>
        </div>
        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor="address-locality">
                  {I18n.t('discovery.addressBook.form.locality')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  id="address-locality"
                  fieldName="locality"
                  inputName="locality"
                  inputType="text"
                  inputValue={this.props.addressFormData.locality}
                  className="wide required"
                  updateField={this.props.onUpdateField}
                  required
                />
              </div>
            </div>
          </div>
          <div className="g-col-6 g-col-mobile-12">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor="address-region">
                  {I18n.t('discovery.addressBook.form.region')}
                </label>
              </div>
              <div className="form-group__fields">
                <RegionSelect
                  id="address-region"
                  countries={this.props.countries}
                  countryCode={this.props.addressFormData.countryCode}
                  value={this.props.addressFormData.region}
                  onChange={this.handleChangeRegion}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="g-container">
          <div className="g-col-6 g-col-mobile-12 scaling-mb-2">

            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor="address-postal-code">
                  {I18n.t('discovery.addressBook.form.postalCode')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  id="address-postal-code"
                  fieldName="postalCode"
                  inputName="postalCode"
                  inputType="text"
                  inputValue={this.props.addressFormData.postalCode}
                  className="wide required"
                  updateField={this.props.onUpdateField}
                  required
                />
              </div>
            </div>

          </div>

          <div className="g-col-6 g-col-mobile-12 scaling-mb-2">
            <div className="form-group">
              <div className="form-group__header">
                <label htmlFor="address-phone">
                  {I18n.t('discovery.addressBook.form.phone')}
                </label>
              </div>
              <div className="form-group__fields">
                <ControlledInput
                  id="address-phone"
                  fieldName="phone"
                  inputName="phone"
                  inputType="text"
                  inputValue={this.props.addressFormData.phone}
                  className="wide required"
                  updateField={this.props.onUpdateField}
                  required
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  renderAddressFields() {
    return (
      <>
        <AddressFormFields
          address={this.props.addressFormData}
          countries={decamelizeKeys(this.props.countries)}
          updateField={this.props.onUpdateField}
          phoneFieldRequired
          shouldTrackView={this.props.shouldTrackView}
        />
        {this.existingAddressForm()}
      </>
    );
  }

  render() {
    return (
      <form
        className="address-form"
        ref={this.props.formRef}
      >
        {this.renderErrors()}

        {this.renderAddressFields()}

        {this.renderEmailInput()}

        <div className="scaling-mt-2">
          {this.renderDefaultCheckbox()}
        </div>
      </form>
    );
  }
}

export default withUserContext(AddressForm);
